import React, { useState, useEffect } from 'react';
import {
  Wallet,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Eye,
  FileDown,
  ArrowUpRight,
  ArrowDownRight,
  Building,
  Users,
  Calendar,
  Filter,
  Search,
  X,
  Loader2,
  BanknoteIcon,
  DollarSign,
  TrendingUp
} from 'lucide-react';

const DVASettlements = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeView, setActiveView] = useState('all'); // 'all', 'pending', 'settled', 'failed'
  const [filters, setFilters] = useState({
    merchantId: '',
    startDate: '',
    endDate: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError('');

      // Build status filter based on active view
      let statusFilter = filters.status;
      if (activeView === 'pending' && !filters.status) {
        statusFilter = 'pending';
      } else if (activeView === 'settled' && !filters.status) {
        statusFilter = 'settled';
      } else if (activeView === 'failed' && !filters.status) {
        statusFilter = 'failed';
      }

      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: 20,
        ...(statusFilter && { settlementStatus: statusFilter }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.merchantId && { merchantId: filters.merchantId })
      });

      const response = await fetch(`/api/dva/admin/transactions?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setTransactions(data.transactions || []);
        if (data.pagination) {
          setPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.error || 'Failed to load transactions');
      }
    } catch (err) {
      setError('Failed to load DVA transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/dva/admin/stats', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load DVA stats:', err);
    }
  };

  const releaseSettlement = async (transactionId) => {
    try {
      const response = await fetch(`/api/dva/admin/settlements/${transactionId}/release`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Settlement released successfully');
        setTimeout(() => setSuccess(''), 3000);
        loadTransactions();
        loadStats();
      } else {
        setError(data.error || 'Failed to release settlement');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError('Failed to release settlement');
      setTimeout(() => setError(''), 3000);
    }
  };

  const exportToCSV = () => {
    if (transactions.length === 0) {
      setError('No transactions to export');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const headers = [
      'Transaction ID',
      'Paystack Reference',
      'Merchant',
      'Store',
      'Amount Received',
      'Transaction Fee',
      'Merchant Gets',
      'Charge Rate %',
      'Status',
      'Sender Name',
      'Sender Bank',
      'Account Number',
      'Date'
    ];

    const data = transactions.map(t => [
      t.id,
      t.paystackReference || 'N/A',
      t.merchant?.username || t.merchant?.email || 'N/A',
      t.store?.name || 'N/A',
      t.amount,
      t.serviceCharge,
      t.merchantAmount,
      t.serviceChargeRate,
      t.settlementStatus,
      t.senderName || 'N/A',
      t.senderBank || 'N/A',
      t.virtualAccountNumber || 'N/A',
      new Date(t.createdAt).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `dva-transactions-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess('Transactions exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: { bg: '#fef3c7', color: '#92400e', icon: Clock },
      held: { bg: '#fee2e2', color: '#dc2626', icon: AlertCircle },
      settled: { bg: '#dcfce7', color: '#166534', icon: CheckCircle2 },
      failed: { bg: '#fee2e2', color: '#dc2626', icon: XCircle }
    };

    const style = statusStyles[status] || statusStyles.pending;
    const Icon = style.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        background: style.bg,
        color: style.color,
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: '500',
        textTransform: 'capitalize'
      }}>
        <Icon size={12} />
        {status}
      </span>
    );
  };

  const viewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };

  const clearFilters = () => {
    setFilters({
      merchantId: '',
      startDate: '',
      endDate: '',
      status: ''
    });
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [activeView, filters.status, filters.startDate, filters.endDate]);

  useEffect(() => {
    loadTransactions();
  }, [activeView, pagination.current]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
            DVA Settlements
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => { loadStats(); loadTransactions(); }}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              disabled={transactions.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: transactions.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <FileDown size={16} />
              Export CSV
            </button>
          </div>
        </div>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
          Track bank transfer payments and RevStar transaction fees
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fee2e2',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#16a34a'
        }}>
          <CheckCircle2 size={18} />
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div
          onClick={() => setActiveView('all')}
          style={{
            background: activeView === 'all' ? '#eff6ff' : 'white',
            borderRadius: '12px',
            padding: '20px',
            border: activeView === 'all' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#dbeafe', borderRadius: '10px' }}>
              <BanknoteIcon size={20} color="#2563eb" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Total Volume</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
            {formatCurrency(stats?.totalVolume || 0)}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            {stats?.totalTransactions || 0} transactions
          </div>
        </div>

        <div
          onClick={() => setActiveView('pending')}
          style={{
            background: activeView === 'pending' ? '#fffbeb' : 'white',
            borderRadius: '12px',
            padding: '20px',
            border: activeView === 'pending' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#fef3c7', borderRadius: '10px' }}>
              <Clock size={20} color="#d97706" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Pending</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
            {formatCurrency(stats?.pendingAmount || 0)}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            {stats?.pendingCount || 0} awaiting settlement
          </div>
        </div>

        <div
          onClick={() => setActiveView('settled')}
          style={{
            background: activeView === 'settled' ? '#f0fdf4' : 'white',
            borderRadius: '12px',
            padding: '20px',
            border: activeView === 'settled' ? '2px solid #10b981' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#dcfce7', borderRadius: '10px' }}>
              <CheckCircle2 size={20} color="#16a34a" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Settled</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>
            {formatCurrency(stats?.settledAmount || 0)}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            {stats?.settledCount || 0} completed
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px' }}>
              <TrendingUp size={20} color="white" />
            </div>
            <span style={{ fontSize: '14px', opacity: 0.9 }}>RevStar Earnings</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>
            {formatCurrency(stats?.totalServiceCharge || 0)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
            Total transaction fees collected
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '16px' }}>
        {[
          { id: 'all', label: 'All Transactions', icon: BanknoteIcon },
          { id: 'pending', label: 'Pending', icon: Clock },
          { id: 'settled', label: 'Settled', icon: CheckCircle2 },
          { id: 'failed', label: 'Failed', icon: XCircle }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: activeView === tab.id ? '#111827' : 'transparent',
              color: activeView === tab.id ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} color="#6b7280" />
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Filters</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#6b7280' }}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              style={{
                padding: '6px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#6b7280' }}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              style={{
                padding: '6px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <button
            onClick={clearFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#6b7280'
            }}
          >
            <X size={14} />
            Clear
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
            DVA Transactions
          </h2>
          <span style={{
            padding: '4px 12px',
            background: '#f3f4f6',
            color: '#374151',
            borderRadius: '9999px',
            fontSize: '13px',
            fontWeight: '500'
          }}>
            {pagination.total} total
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Loader2 size={32} className="animate-spin" style={{ color: '#6b7280', margin: '0 auto 16px' }} />
            <p style={{ color: '#6b7280' }}>Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: '#f3f4f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <BanknoteIcon size={32} color="#6b7280" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
              No transactions found
            </h3>
            <p style={{ color: '#6b7280' }}>
              DVA transactions will appear here when customers make bank transfers
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Merchant</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Sender</th>
                  <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Transaction Fee</th>
                  <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Merchant Gets</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr
                    key={transaction.id}
                    style={{
                      borderBottom: index < transactions.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <td style={{ padding: '16px 20px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#111827' }}>
                          {transaction.merchant?.username || transaction.merchant?.email || 'N/A'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {transaction.store?.name || 'No Store'}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#374151' }}>
                          {transaction.senderName || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {transaction.senderBank || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>
                        {formatCurrency(transaction.amount)}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <div style={{ fontWeight: '500', color: '#10b981' }}>
                        {formatCurrency(transaction.serviceCharge)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        {transaction.serviceChargeRate}%
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <div style={{ fontWeight: '600', color: '#374151' }}>
                        {formatCurrency(transaction.merchantAmount)}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      {getStatusBadge(transaction.settlementStatus)}
                    </td>
                    <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: '13px' }}>
                      {formatDate(transaction.createdAt)}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        <button
                          onClick={() => viewDetails(transaction)}
                          style={{
                            padding: '6px 10px',
                            background: 'transparent',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            color: '#374151'
                          }}
                        >
                          <Eye size={14} />
                          View
                        </button>
                        {transaction.settlementStatus === 'held' && (
                          <button
                            onClick={() => releaseSettlement(transaction.id)}
                            style={{
                              padding: '6px 10px',
                              background: '#10b981',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: 'white'
                            }}
                          >
                            <CheckCircle2 size={14} />
                            Release
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Page {pagination.current} of {pagination.pages} ({pagination.total} total)
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
                disabled={pagination.current === 1}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: pagination.current === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  opacity: pagination.current === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
                disabled={pagination.current >= pagination.pages}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: pagination.current >= pagination.pages ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  opacity: pagination.current >= pagination.pages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {showModal && selectedTransaction && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                Transaction Details
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Status Badge */}
              <div style={{ marginBottom: '20px' }}>
                {getStatusBadge(selectedTransaction.settlementStatus)}
              </div>

              {/* Financial Summary */}
              <div style={{
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Amount Received</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                      {formatCurrency(selectedTransaction.amount)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Transaction Fee ({selectedTransaction.serviceChargeRate}%)
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                      {formatCurrency(selectedTransaction.serviceCharge)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Merchant Gets</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#374151' }}>
                      {formatCurrency(selectedTransaction.merchantAmount)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Transaction ID</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace' }}>
                    {selectedTransaction.id}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Paystack Reference</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace' }}>
                    {selectedTransaction.paystackReference || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Merchant</div>
                  <div style={{ fontSize: '14px', color: '#111827' }}>
                    {selectedTransaction.merchant?.username || selectedTransaction.merchant?.email || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Store</div>
                  <div style={{ fontSize: '14px', color: '#111827' }}>
                    {selectedTransaction.store?.name || 'No Store'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Sender Name</div>
                  <div style={{ fontSize: '14px', color: '#111827' }}>
                    {selectedTransaction.senderName || 'Unknown'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Sender Bank</div>
                  <div style={{ fontSize: '14px', color: '#111827' }}>
                    {selectedTransaction.senderBank || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Virtual Account</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace' }}>
                    {selectedTransaction.virtualAccountNumber || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Date</div>
                  <div style={{ fontSize: '14px', color: '#111827' }}>
                    {formatDate(selectedTransaction.createdAt)}
                  </div>
                </div>
                {selectedTransaction.orderId && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Order ID</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace' }}>
                      {selectedTransaction.orderId}
                    </div>
                  </div>
                )}
                {selectedTransaction.settlementHoldUntil && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Hold Until</div>
                    <div style={{ fontSize: '14px', color: '#111827' }}>
                      {formatDate(selectedTransaction.settlementHoldUntil)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              {selectedTransaction.settlementStatus === 'held' && (
                <button
                  onClick={() => {
                    releaseSettlement(selectedTransaction.id);
                    closeModal();
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Release Settlement
                </button>
              )}
              <button
                onClick={closeModal}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default DVASettlements;
